import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ValuationResults = ({ results, craneData, onSave }) => {
  if (!results) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const getConfidenceColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-warning';
    return 'text-error';
  };

  const getConfidenceBackground = (score) => {
    if (score >= 90) return 'bg-success/10';
    if (score >= 75) return 'bg-warning/10';
    return 'bg-error/10';
  };

  return (
    <div className="space-y-6">
      {/* Main Valuation Card */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={24} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">Valuation Results</h2>
              <p className="text-text-secondary">{craneData?.make} {craneData?.model} ({craneData?.year})</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            onClick={onSave}
            iconName="Save"
            iconPosition="left"
            size="sm"
          >
            Save Configuration
          </Button>
        </div>

        {/* Main Value Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center p-6 bg-muted rounded-lg">
            <div className="text-3xl font-bold text-primary mb-2">
              {formatCurrency(results?.fairMarketValue)}
            </div>
            <div className="text-sm font-medium text-text-primary mb-1">Fair Market Value</div>
            <div className="text-xs text-text-secondary">Based on current market conditions</div>
          </div>
          
          <div className="text-center p-6 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary mb-2">
              {formatCurrency(results?.valueRange?.min)} - {formatCurrency(results?.valueRange?.max)}
            </div>
            <div className="text-sm font-medium text-text-primary mb-1">Value Range</div>
            <div className="text-xs text-text-secondary">Expected market pricing spread</div>
          </div>
          
          <div className="text-center p-6 bg-muted rounded-lg">
            <div className={`text-2xl font-bold mb-2 ${getConfidenceColor(results?.confidenceScore)}`}>
              {results?.confidenceScore}%
            </div>
            <div className="text-sm font-medium text-text-primary mb-1">Confidence Score</div>
            <div className="text-xs text-text-secondary">Valuation accuracy estimate</div>
          </div>
        </div>

        {/* Confidence Indicator */}
        <div className={`p-4 rounded-lg ${getConfidenceBackground(results?.confidenceScore)} border border-opacity-20`}>
          <div className="flex items-center space-x-3">
            <Icon 
              name={results?.confidenceScore >= 90 ? 'CheckCircle' : results?.confidenceScore >= 75 ? 'AlertTriangle' : 'AlertCircle'} 
              size={20} 
              className={getConfidenceColor(results?.confidenceScore)}
            />
            <div>
              <div className={`font-medium ${getConfidenceColor(results?.confidenceScore)}`}>
                {results?.confidenceScore >= 90 ? 'High Confidence' : 
                 results?.confidenceScore >= 75 ? 'Moderate Confidence' : 'Lower Confidence'}
              </div>
              <div className="text-sm text-text-secondary">
                {results?.confidenceScore >= 90 ? 'Excellent data quality with comprehensive market comparison' :
                 results?.confidenceScore >= 75 ? 'Good data quality with adequate market comparison': 'Limited data available - consider additional market research'}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Market Factors */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} className="text-accent" />
          <span>Market Factors</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary">Market Demand</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                results?.marketFactors?.demand === 'High' ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'
              }`}>
                {results?.marketFactors?.demand}
              </span>
            </div>
            <p className="text-xs text-text-secondary">Current market demand for this crane type</p>
          </div>
          
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary">Supply Level</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                results?.marketFactors?.supply === 'Limited' ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'
              }`}>
                {results?.marketFactors?.supply}
              </span>
            </div>
            <p className="text-xs text-text-secondary">Available inventory in the market</p>
          </div>
          
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary">Price Trend</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                results?.marketFactors?.trend === 'Increasing' ? 'bg-success/20 text-success' : 'bg-primary/20 text-primary'
              }`}>
                {results?.marketFactors?.trend}
              </span>
            </div>
            <p className="text-xs text-text-secondary">6-month pricing trend direction</p>
          </div>
        </div>
      </div>
      {/* Regional Variation */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="MapPin" size={20} className="text-accent" />
          <span>Regional Price Variation</span>
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium text-text-primary">Current Region ({craneData?.currentLocation || 'Not specified'})</span>
            <span className="text-sm font-semibold text-text-primary">Baseline</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium text-text-primary">Highest Value Markets</span>
            <span className="text-sm font-semibold text-success">+{results?.regionalVariation?.high}%</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium text-text-primary">Lower Value Markets</span>
            <span className="text-sm font-semibold text-error">{results?.regionalVariation?.low}%</span>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <Icon name="Info" size={16} className="inline mr-1" />
            Regional variations are based on historical sales data and current market conditions in different geographic areas.
          </p>
        </div>
      </div>
      {/* Recommendations */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Lightbulb" size={20} className="text-accent" />
          <span>Valuation Insights & Recommendations</span>
        </h3>
        
        <div className="space-y-3">
          {results?.recommendations?.map((recommendation, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
              <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <span className="text-sm text-text-primary">{recommendation}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Key Specifications Summary */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="FileText" size={20} className="text-accent" />
          <span>Crane Specifications</span>
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary">{craneData?.capacity || 'N/A'}</div>
            <div className="text-xs text-text-secondary">Capacity (tons)</div>
          </div>
          
          <div className="text-center p-3 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary">{craneData?.hours?.toLocaleString() || 'N/A'}</div>
            <div className="text-xs text-text-secondary">Operating Hours</div>
          </div>
          
          <div className="text-center p-3 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary">{craneData?.condition || 'N/A'}/5</div>
            <div className="text-xs text-text-secondary">Condition Rating</div>
          </div>
          
          <div className="text-center p-3 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary">{craneData?.boomLength || 'N/A'}ft</div>
            <div className="text-xs text-text-secondary">Boom Length</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValuationResults;